import { create } from "zustand";
import { chatGatewaySyncService } from "@/services/chatGatewaySyncService";
import { communicationChannelsService } from "@/services/communicationChannelsService";
import { getApiErrorMessage } from "@/utils/apiErrors";

/**
 * Note: there are no separate {@code wa-branch} / {@code wa-routing-*} step
 * kinds. The chat-gateway channel sync + the 3 wa-gateway calls (branch
 * register, OUTBOUND routing, INBOUND routing) are all rolled up under a
 * single {@code channel} step labelled "WhatsApp" in the progress panel.
 *
 * The runner itself is channel-agnostic: it always walks
 * LE → branches → users → channel → flow-onboarding → activation. Everything
 * that differs between WhatsApp and Email is captured by a
 * {@link ChannelSyncAdapter} — see {@link whatsappAdapter} / {@link emailAdapter}.
 */
export type SyncStepKind =
  | "legal-entity"
  | "branch"
  | "users"
  | "channel"
  | "flow-onboarding"
  | "activation";
export type SyncStepStatus = "pending" | "running" | "done" | "error";

export interface SyncStep {
  /** Unique within a job — use this (not {@link kind}) to address a step. */
  id: string;
  kind: SyncStepKind;
  label: string;
  status: SyncStepStatus;
  error?: string;
}

export interface SyncJob {
  id: string;
  /** Which row in the UI started the sync — controls which row's button spins. */
  targetKind: "LE" | "BR";
  targetId: string;
  targetName: string;
  /** Channel-specific caption for the progress card (WhatsApp phone / email id). */
  subtitle: string;
  startedAt: number;
  finishedAt?: number;
  steps: SyncStep[];
}

export interface SyncLegalEntityInput {
  id: string;
  name: string;
  primaryEmail?: string | null;
  parentId?: string | null;
  sourceCrm?: string | null;
}

export interface SyncBranchInput {
  id: string;
  name: string;
  email?: string | null;
}

/** WhatsApp-specific channel payload for the {@code channel} + {@code activation} steps. */
export interface WhatsappChannelInput {
  /** {@code null} → LE-scoped channel (atlas-api forwards {@code branchId: null}). */
  branchId: string | null;
  whatsappPhone: string;
  channelId: string;
  /** Meta Phone Number ID — required to register with wa-gateway. */
  whatsappPhoneNumberId?: string | null;
  /** Long-lived WhatsApp Business access token. */
  whatsappAccessToken?: string | null;
  /** Meta WhatsApp Business Account ID. */
  whatsappBusinessAccountId?: string | null;
}

/** Email-specific channel payload for the {@code channel} + {@code activation} steps. */
export interface EmailChannelInput {
  branchId: string | null;
  channelId: string;
  emailId: string;
  inboxLabel?: string | null;
}

interface BaseSyncInput {
  legalEntity: SyncLegalEntityInput;
  /**
   * All branches that should be pushed to chat-gateway as part of this run.
   * For an LE-row sync this is the LE's whole branch list; for a Branch-row
   * sync this is just the single branch.
   */
  branches: SyncBranchInput[];
}

export interface WhatsappSyncInput extends BaseSyncInput {
  channelType: "WHATSAPP";
  channel: WhatsappChannelInput;
}

export interface EmailSyncInput extends BaseSyncInput {
  channelType: "EMAIL";
  channel: EmailChannelInput;
}

export type StartSyncInput = WhatsappSyncInput | EmailSyncInput;

/**
 * Everything that differs between channels. The shared runner
 * ({@link runSync}) owns the org-structure steps (LE / branch / users /
 * flow-onboarding); each adapter only supplies the channel-specific calls.
 */
interface ChannelSyncAdapter<I extends StartSyncInput> {
  channelType: I["channelType"];
  /** Caption for the progress card (e.g. the phone number or email address). */
  subtitle: (input: I) => string;
  /** Label for the single rolled-up {@code channel} step. */
  channelStepLabel: (input: I) => string;
  /** Optional fire-and-forget pre-step, run before the shared sequence. */
  preSync?: (input: I) => void;
  /** Channel-specific calls behind the {@code channel} step. */
  syncChannel: (input: I) => Promise<unknown>;
  /** Channel-specific activation (the {@code activation} step). */
  activate: (input: I) => Promise<unknown>;
}

interface State {
  jobs: SyncJob[];
  startSync: (input: StartSyncInput) => string;
  dismissJob: (id: string) => void;
  clearFinished: () => void;
}

const nowId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const STEP_ID_LE    = "le";
const STEP_ID_USERS = "users";
const STEP_ID_CH    = "ch";
const STEP_ID_FLOW  = "flow";
const STEP_ID_ACTIVE= "active";
const branchStepId = (branchId: string) => `br:${branchId}`;

/** WhatsApp: chat-gateway channel + wa-gateway branch/routing + Atlas activation. */
const whatsappAdapter: ChannelSyncAdapter<WhatsappSyncInput> = {
  channelType: "WHATSAPP",
  subtitle: (i) => i.channel.whatsappPhone,
  channelStepLabel: (i) => `WhatsApp · ${i.channel.whatsappPhone}`,

  // Template sync might have been skipped during onboarding if templates
  // already existed — fire it directly, best-effort. Failure is non-blocking
  // and never stops the sync chain.
  preSync: (i) => {
    const token = i.channel.whatsappAccessToken;
    if (!token) return;
    const credKey = i.channel.branchId ?? i.legalEntity.id;
    communicationChannelsService
      .syncTemplates(credKey, i.channel.branchId ? "BR" : "LE", token)
      .catch((e) => console.error("Failed to sync templates", e));
  },

  // All four downstream calls share one progress entry so the operator sees a
  // single "WhatsApp" step. If any sub-call fails the step turns red with the
  // upstream reason — the others below it don't run.
  syncChannel: async (i) => {
    const c = i.channel;

    // 4a — chat-gateway communication-channel sync.
    await chatGatewaySyncService.syncChannel({
      channelType: "WHATSAPP",
      legalEntityId: i.legalEntity.id,
      branchId: c.branchId,
      whatsappPhone: c.whatsappPhone,
      whatsappEnabled: true,
      sourceCrm: i.legalEntity.sourceCrm ?? "atlas",
      channelId: c.channelId,
      whatsappPhoneNumberId: c.whatsappPhoneNumberId ?? null,
      whatsappAccessToken: c.whatsappAccessToken ?? null,
      whatsappBusinessAccountId: c.whatsappBusinessAccountId ?? null,
    });

    // 4b — wa-gateway branch register + 2 routing rules. Skipped only when the
    // operator hasn't saved full credentials.
    if (!c.whatsappPhoneNumberId || !c.whatsappBusinessAccountId || !c.whatsappAccessToken) {
      throw new Error(
        "WhatsApp credentials (Phone Number ID, WABA ID, Access Token) are incomplete — fill them in on the Communication Channels page and save before syncing.",
      );
    }

    const channelTarget = c.branchId
      ? i.branches.find((b) => b.id === c.branchId)
      : undefined;
    const waName = channelTarget?.name ?? i.legalEntity.name;

    await chatGatewaySyncService.whatsappBranch({
      name: waName,
      legalEntityId: i.legalEntity.id,
      branchId: c.branchId,
      channelId: c.channelId,
      whatsappPhoneNumber: c.whatsappPhone,
      whatsappPhoneNumberId: c.whatsappPhoneNumberId,
      whatsappBusinessAccountId: c.whatsappBusinessAccountId,
      whatsappAccessToken: c.whatsappAccessToken,
      active: true,
    });

    // atlas-api resolves wa-gateway's internal branch id from the
    // phoneNumberId — the UI doesn't carry the wa-gateway primary key.
    await chatGatewaySyncService.whatsappRoutingRule({
      whatsappPhoneNumberId: c.whatsappPhoneNumberId,
      environmentName: "pubsub-in",
      direction: "OUTBOUND",
      senderPhoneNumber: "*",
      enabled: true,
    });

    await chatGatewaySyncService.whatsappRoutingRule({
      whatsappPhoneNumberId: c.whatsappPhoneNumberId,
      environmentName: "pubsub-out",
      direction: "INBOUND",
      senderPhoneNumber: "*",
      enabled: true,
    });
  },

  activate: (i) =>
    communicationChannelsService.upsert({
      channelType: "WHATSAPP",
      legalEntityId: i.legalEntity.id,
      branchId: i.channel.branchId,
      whatsappPhone: i.channel.whatsappPhone,
      sourceCrm: i.legalEntity.sourceCrm ?? "atlas",
      whatsappEnabled: true,
      active: true,
    }),
};

/** Email: shares the org-structure backbone; Atlas row then gateway registration. */
const emailAdapter: ChannelSyncAdapter<EmailSyncInput> = {
  channelType: "EMAIL",
  subtitle: (i) => i.channel.emailId,
  channelStepLabel: (i) => `Email · ${i.channel.emailId}`,

  // No template pre-sync for email.

  // Persist the Atlas channel row first, so the gateway registration below
  // always follows a row that exists and is marked enabled — Save writes the
  // same row, but Enable Conversations must not depend on it having been
  // clicked (or on the address having stayed unchanged since).
  syncChannel: (i) => {
    const c = i.channel;
    return communicationChannelsService.upsertEmail({
      channelType: "EMAIL",
      channelId: c.channelId || null,
      legalEntityId: i.legalEntity.id,
      branchId: c.branchId,
      sourceCrm: i.legalEntity.sourceCrm ?? "atlas",
      emailEnabled: true,
      emailId: c.emailId,
      inboxLabel: c.inboxLabel ?? null,
    });
  },

  // Register the email channel with bridge-chat-gateway. This mirrors
  // WhatsApp's chat-gateway channel sync and is the final call of the run.
  //
  activate: (i) => {
    const c = i.channel;
    return chatGatewaySyncService.syncEmailChannel({
      channelType: "EMAIL",
      legalEntityId: i.legalEntity.id,
      branchId: c.branchId,
      emailId: c.emailId,
      emailEnabled: true,
      sourceCrm: i.legalEntity.sourceCrm ?? "atlas",
      channelId: c.channelId,
      inboxLabel: c.inboxLabel ?? null,
      // input.branches is already scoped correctly by the caller: the LE's
      // full branch list (incl. sub-entities) for an LE sync, or just the
      // single branch for a branch-scoped sync.
      branches: Object.fromEntries(i.branches.map((b) => [b.id, b.name])),
    });
  },
};

/**
 * CRM-synced branches (e.g. Reapit) can arrive without a name, which the
 * gateway rejects ("name: must not be blank"). Branches are mandatory for
 * inboxes, so derive a name instead of skipping the branch.
 */
function withResolvedBranchName(b: SyncBranchInput): SyncBranchInput {
  if (b.name?.trim()) return b;
  const local = b.email?.split("@")[0]?.trim();
  return { ...b, name: local || `Branch ${b.id.slice(0, 8)}` };
}

/**
 * An adapter bound to a concrete input — every channel-specific detail
 * pre-resolved to a channel-agnostic shape the runner can consume without
 * ever re-touching the discriminated union.
 */
interface SyncPlan {
  subtitle: string;
  channelStepLabel: string;
  preSync?: () => void;
  syncChannel: () => Promise<unknown>;
  activate: () => Promise<unknown>;
}

function makePlan<I extends StartSyncInput>(adapter: ChannelSyncAdapter<I>, input: I): SyncPlan {
  return {
    subtitle: adapter.subtitle(input),
    channelStepLabel: adapter.channelStepLabel(input),
    preSync: adapter.preSync ? () => adapter.preSync!(input) : undefined,
    syncChannel: () => adapter.syncChannel(input),
    activate: () => adapter.activate(input),
  };
}

/** Binds the right adapter to the input; the {@code case} narrows both together. */
function planFor(input: StartSyncInput): SyncPlan {
  switch (input.channelType) {
    case "WHATSAPP":
      return makePlan(whatsappAdapter, input);
    case "EMAIL":
      return makePlan(emailAdapter, input);
  }
}

/**
 * Drives the sequential chat-gateway sync flow (LE → every branch → channel)
 * and exposes per-step status so the global progress panel can render live
 * updates. The store outlives any single component, so a user kicking off a
 * sync and navigating away keeps seeing progress.
 */
export const useChatGatewaySyncStore = create<State>((set) => {
  const updateStep = (jobId: string, stepId: string, patch: Partial<SyncStep>) => {
    set((state) => ({
      jobs: state.jobs.map((j) =>
        j.id !== jobId
          ? j
          : { ...j, steps: j.steps.map((s) => (s.id === stepId ? { ...s, ...patch } : s)) },
      ),
    }));
  };

  const finishJob = (jobId: string) => {
    set((state) => ({
      jobs: state.jobs.map((j) => (j.id === jobId ? { ...j, finishedAt: Date.now() } : j)),
    }));
  };

  /**
   * Wrap a single async step so step status flips to running → done/error
   * exactly once and a thrown error short-circuits the rest of the run.
   */
  const runStep = async (
    jobId: string,
    stepId: string,
    fn: () => Promise<unknown>,
  ) => {
    updateStep(jobId, stepId, { status: "running" });
    try {
      await fn();
      updateStep(jobId, stepId, { status: "done" });
    } catch (err: unknown) {
      updateStep(jobId, stepId, {
        status: "error",
        error: getApiErrorMessage(err) ?? "Sync failed. Please try again.",
      });
      throw err;
    }
  };

  const runSync = async (
    jobId: string,
    input: StartSyncInput,
    plan: SyncPlan,
  ) => {
    try {
      plan.preSync?.();

      // ── 1. Legal entity ──────────────────────────────────────────────────
      await runStep(jobId, STEP_ID_LE, () =>
        chatGatewaySyncService.syncLegalEntity({
          id: input.legalEntity.id,
          legalName: input.legalEntity.name,
          primaryEmail: input.legalEntity.primaryEmail ?? null,
          parentId: input.legalEntity.parentId ?? null,
        }),
      );

      // ── 2. Every branch, in order ────────────────────────────────────────
      // Branch email is optional on Profile; send null/empty when absent and let
      // chat-gateway apply its own rules.
      for (const br of input.branches) {
        await runStep(jobId, branchStepId(br.id), () =>
          chatGatewaySyncService.syncBranch({
            id: br.id,
            legalEntityId: input.legalEntity.id,
            name: br.name,
            email: br.email?.trim() || null,
            channelType: input.channelType,
          }),
        );
      }

      // ── 3. Users ─────────────────────────────────────────────────────────
      // Fetches all Atlas users for this LE from atlas-api and bulk-pushes
      // them to bridge-chat-gateway. Returns done (not error) if there are
      // no users for the LE.
      await runStep(jobId, STEP_ID_USERS, () =>
        chatGatewaySyncService.syncUsers({
          legalEntityId: input.legalEntity?.id,
          ...(input.channel?.branchId ? { branchId: input.channel.branchId } : {}),
        }),
      );

      // ── 4. Channel (channel-specific — see the adapter) ──────────────────
      await runStep(jobId, STEP_ID_CH, () => plan.syncChannel());

      // ── 5. Flow onboarding ───────────────────────────────────────────────
      await runStep(jobId, STEP_ID_FLOW, () =>
        chatGatewaySyncService.flowOnboarding({
          legalEntityId: input.legalEntity.id,
          branchIds: input.branches.map((b) => b.id),
        }),
      );

      // ── 6. Activation (channel-specific — see the adapter) ───────────────
      await runStep(jobId, STEP_ID_ACTIVE, () => plan.activate());
    } catch {
      // Step error already recorded; just stop the chain.
    } finally {
      finishJob(jobId);
    }
  };

  return {
    jobs: [],

    startSync: (rawInput) => {
      const input = {
        ...rawInput,
        branches: rawInput.branches.map(withResolvedBranchName),
      } as StartSyncInput;
      const id = nowId();
      const plan = planFor(input);
      const branchId = input.channel.branchId;
      const isBranchScoped = branchId !== null;

      const steps: SyncStep[] = [
        {
          id: STEP_ID_LE,
          kind: "legal-entity",
          label: `Legal Entity · ${input.legalEntity.name}`,
          status: "pending",
        },
        ...input.branches.map<SyncStep>((br) => ({
          id: branchStepId(br.id),
          kind: "branch",
          label: `Branch · ${br.name}`,
          status: "pending",
        })),
        {
          id: STEP_ID_USERS,
          kind: "users",
          label: `Users · ${input.legalEntity.name}`,
          status: "pending",
        },
        {
          id: STEP_ID_CH,
          kind: "channel",
          label: plan.channelStepLabel,
          status: "pending",
        },
        {
          id: STEP_ID_FLOW,
          kind: "flow-onboarding",
          label: `Flow onboarding · agency attributes`,
          status: "pending",
        },
        {
          id: STEP_ID_ACTIVE,
          kind: "activation",
          label: `Activation · enable in UI`,
          status: "pending",
        },
      ];

      const target = isBranchScoped
        ? input.branches.find((b) => b.id === branchId)
        : undefined;

      const job: SyncJob = {
        id,
        targetKind: isBranchScoped ? "BR" : "LE",
        targetId: isBranchScoped ? branchId! : input.legalEntity.id,
        targetName: isBranchScoped ? target?.name ?? "Branch" : input.legalEntity.name,
        subtitle: plan.subtitle,
        startedAt: Date.now(),
        steps,
      };

      set((state) => ({ jobs: [...state.jobs, job] }));

      // Fire-and-forget — UI subscribes to `jobs` for live status.
      void runSync(id, input, plan);

      return id;
    },

    dismissJob: (id) => {
      set((state) => ({ jobs: state.jobs.filter((j) => j.id !== id) }));
    },

    clearFinished: () => {
      set((state) => ({ jobs: state.jobs.filter((j) => !j.finishedAt) }));
    },
  };
});
